import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';

import Icon from '../../components/AppIcon';
import { getTranslation } from '../../utils/translations';
import advancedBillingService from '../../services/advancedBillingService';
import DiscountRulesSection from './components/DiscountRulesSection';
import PaymentPhasesSection from './components/PaymentPhasesSection';
import CountryManagementSection from './components/CountryManagementSection';
import BillingAnalytics from './components/BillingAnalytics';

const AdvancedBillingManagement = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('language') || 'FR';
  });
  const t = getTranslation(currentLanguage);

  const [activeTab, setActiveTab] = useState('discounts');
  const [discountRules, setDiscountRules] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesData, countriesData] = await Promise.all([
        advancedBillingService?.getAllDiscountRules(),
        advancedBillingService?.getAllCountries()
      ]);
      setDiscountRules(rulesData);
      setCountries(countriesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const breadcrumbItems = [
    { label: t?.dashboard, path: '/dashboard' },
    { label: t?.billing?.title, path: '/billing-overview' },
    { label: t?.billing?.title, path: '/advanced-billing-management' }
  ];

  const tabs = [
    { id: 'discounts', label: t?.billing?.discountRules, icon: 'Tag' },
    { id: 'phases', label: t?.billing?.paymentPhases, icon: 'Calendar' },
    {/*{ id: 'countries', label: t?.billing?.countries, icon: 'Globe' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' } */}
  ];

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
             <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <Breadcrumb items={breadcrumbItems} />
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mt-2">
                  {t?.advancedBilling}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {currentLanguage === 'FR' && 'Gestion des remises automatiques et paiements par phase'}
                  {currentLanguage === 'EN' && 'Manage automatic discounts and phase-based payments'}
                  {currentLanguage === 'PL' && 'Zarządzaj automatycznymi rabatami i płatnościami fazowymi'}
                </p>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="bg-card border border-border rounded-lg p-1 flex flex-wrap gap-1">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="animate-fade-in">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {activeTab === 'discounts' && (
                    <DiscountRulesSection
                      discountRules={discountRules}
                      countries={countries}
                      onRefresh={fetchData}
                      language={currentLanguage}
                    />
                  )}
                  {activeTab === 'phases' && (
                    <PaymentPhasesSection
                      language={currentLanguage}
                    />
                  )}
                  {/* {activeTab === 'countries' && (
                    <CountryManagementSection
                      countries={countries}
                      onRefresh={fetchData}
                      language={currentLanguage}
                    />
                  )}
                  {activeTab === 'analytics' && (
                    <BillingAnalytics
                      discountRules={discountRules}
                      language={currentLanguage}
                    />
                  )}*/}
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdvancedBillingManagement;